import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { waitForFundSource } from "../assignFundSource";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";

const STEPS = [
  { key: "identity", label: "Verifying Identity", delay: 0 },
  { key: "income", label: "Analysing Income", delay: 1400 },
  { key: "credit", label: "Checking Credit Profile", delay: 2800 },
  { key: "eligibility", label: "Calculating Eligibility", delay: 4200 },
];

function EligibilityEvaluating() {
  const navigate = useNavigate();

  const userData = useEligibilityData();

  const [completedSteps, setCompletedSteps] = useState<number>(-1);
  const [animDone, setAnimDone] = useState(false);
  const [apiDone, setApiDone] = useState(false);
  const [rulesResult, setRulesResult] = useState<string>("");

  useEffect(() => {
    STEPS.forEach((step, idx) => {
      setTimeout(() => setCompletedSteps(idx), step.delay + 900);
    });
    const timer = setTimeout(() => setAnimDone(true), 5100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const callEvaluate = async () => {
      await waitForFundSource();
      const token = sessionStorage.getItem("eligibility_auth_token");
      const userId = userData?.userId;
      const applicationId = userData?.applicationId;
      try {
        const res = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/application/evaluate-rules-eligibility`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId, applicationId }),
        });
        const result = await res.json();
        setRulesResult(result?.data?.rulesResult || result?.data || "");
      } catch {
        setRulesResult("error");
      } finally {
        setApiDone(true);
      }
    };
    callEvaluate();
  }, [userData?.userId, userData?.applicationId]);

  const done = animDone && apiDone;

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => {
      if (rulesResult === "green") {
        navigate("/eligibility/congratulations", { replace: true, state: { data: userData } });
      } else if (rulesResult === "red") {
        navigate("/eligibility/rejected", { replace: true, state: { data: userData } });
      } else {
        navigate("/eligibility/under-review", { replace: true, state: { data: userData } });
      }
    }, 700);
    return () => clearTimeout(t);
  }, [done]);

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Spinner badge ── */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "0.5rem", paddingBottom: "0.75rem" }}>
                <div
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "20px",
                    background: "linear-gradient(135deg, #d32028 0%, #a01820 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(211,32,40,0.28)",
                    marginBottom: "1rem",
                  }}
                >
                  {done ? (
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <path d="M8 18L15 25L28 11" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                      <circle cx="17" cy="17" r="13" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                      <path d="M17 4C24.18 4 30 9.82 30 17" stroke="white" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  )}
                </div>

                <p style={{ fontSize: "1.2rem", fontWeight: 800, color: "#1a1a2e", margin: "0 0 0.3rem", fontFamily: "Outfit, sans-serif", textAlign: "center", letterSpacing: "-0.3px" }}>
                  {done ? "Evaluation Complete!" : "Evaluating Your Application"}
                </p>
                <p style={{ fontSize: "0.82rem", color: "#888", margin: 0, textAlign: "center", lineHeight: 1.5 }}>
                  {done ? "Redirecting you shortly..." : "Please wait while we process your details."}
                </p>
              </div>

              {/* ── Steps ── */}
              <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: "14px", padding: "1rem 1.15rem" }}>
                {STEPS.map((step, idx) => {
                  const isComplete = completedSteps >= idx;
                  const isActive = completedSteps === idx - 1 && !done;
                  const isPending = completedSteps < idx - 1;

                  return (
                    <div
                      key={step.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.85rem",
                        padding: "0.65rem 0",
                        borderBottom: idx < STEPS.length - 1 ? "1px solid #f3f4f6" : "none",
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isComplete ? "#d32028" : isActive ? "transparent" : "#f3f4f6",
                          border: isActive ? "2.5px solid #d32028" : "none",
                          transition: "all 0.3s",
                        }}
                      >
                        {isComplete ? (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : isActive ? (
                          <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#d32028", animation: "pulse 1.2s ease-in-out infinite" }} />
                        ) : (
                          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#d0d0d0" }} />
                        )}
                      </div>

                      <span
                        style={{
                          fontSize: "0.88rem",
                          fontWeight: isComplete || isActive ? 700 : 400,
                          color: isComplete ? "#1a1a2e" : isActive ? "#d32028" : "#bbb",
                          fontFamily: "Outfit, sans-serif",
                          transition: "color 0.3s",
                          flex: 1,
                        }}
                      >
                        {step.label}
                      </span>

                      {isComplete && (
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#22c55e", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "20px", padding: "0.15rem 0.55rem", whiteSpace: "nowrap" }}>
                          Done
                        </span>
                      )}
                      {isActive && (
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#d32028", background: "#fdecea", border: "1px solid #f9d8d6", borderRadius: "20px", padding: "0.15rem 0.55rem", whiteSpace: "nowrap" }}>
                          Processing
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.3); opacity: 0.6; }
                }
              `}</style>
            </div>

          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityEvaluating;
